import { useId, useLayoutEffect, useRef } from "react";
import { routePath } from "../../navigation/routes";
import { AppLink } from "../navigation/AppLink";
import { keepRowInView } from "../navigation/keepRowInView";
import { cn } from "../ui/utils";
import { CalendarViewLinks } from "./CalendarViewLinks";
import { PRICES_PLACE_TABS, PRICES_TAB_ICONS, pricesTabRoute, type PricesFormProps } from "./prices-places";

// `h-full`: a tile whose label wraps sets its row's height, and the others meet it. Stacked from the top, the three
// icons stay on one line and the labels start on one; the top padding is what centring gave a one-line tile.
const TILE =
    "flex h-full min-h-14 flex-col items-center justify-start gap-1 rounded-md px-1 pt-2.5 pb-1.5 text-center text-xs leading-tight outline-none focus-visible:ring-[3px] focus-visible:ring-sidebar-ring/50";

/**
 * Under Prices in the phone's menu: the page's three tabs as tiles and its calendar views as chips, so every place
 * in it is two taps from any page. It opens on the place on screen.
 */
export function PricesPhonePanel({
    currentTab,
    shownView,
    labels,
    connectionId,
    connectionVersion,
    readTracking,
    onOpenTab,
    onOpenView,
}: PricesFormProps) {
    const panelRef = useRef<HTMLDivElement>(null);
    const viewsHeadingId = useId();

    // Once, as the menu opens: a list that moved under the finger afterwards would take a tap meant for another row.
    useLayoutEffect(() => {
        const panel = panelRef.current;
        const nav = panel?.closest("nav");
        if (!panel || !nav) return;
        const current =
            panel.querySelector<HTMLElement>('[data-rail-flyout-item][aria-current="true"]')
            ?? panel.querySelector<HTMLElement>('[aria-current="page"]');
        if (current) keepRowInView(nav, current);
    }, []);

    return (
        <div ref={panelRef} className="rounded-lg border border-sidebar-border p-1">
            <ul aria-label={labels.prices} className="grid grid-cols-3 gap-1">
                {PRICES_PLACE_TABS.map((tab) => {
                    const current = currentTab === tab;
                    const Icon = PRICES_TAB_ICONS[tab];
                    return (
                        <li key={tab} className="min-w-0">
                            <AppLink
                                to={routePath(pricesTabRoute(tab))}
                                active={current}
                                onNavigate={() => onOpenTab(tab)}
                                className={cn(
                                    TILE,
                                    current
                                        ? "bg-accent text-primary font-medium"
                                        : "text-sidebar-foreground/85 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                                )}
                            >
                                <Icon
                                    aria-hidden="true"
                                    className={cn("size-4 shrink-0", current ? "text-primary" : "text-sidebar-foreground/60")}
                                />
                                <span>{labels[tab]}</span>
                            </AppLink>
                        </li>
                    );
                })}
            </ul>
            <CalendarViewLinks
                layout="phone"
                heading={{ id: viewsHeadingId, text: labels.views }}
                shown={shownView}
                connectionId={connectionId}
                connectionVersion={connectionVersion}
                read={readTracking}
                fallback={null}
                onOpenView={onOpenView}
            />
        </div>
    );
}
